import { 
  Box, Button, Divider, Paper, Typography, useTheme, 
  Dialog, DialogTitle, DialogContent, IconButton, TextField, CircularProgress, Tooltip 
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Notification } from "../../db_access/notification";
import { User } from "../../db_access/user";
import { Conversation } from "../../db_access/conversation";
import { Company } from "../../db_access/company";
import { useAuth } from "../../authentication/AuthProvider";
import { FiX, FiSend, FiMessageSquare } from "react-icons/fi";
import DownloadIcon from '@mui/icons-material/Download';
import routing from "../../routing";
import MessageNotification from "./MessageNotification";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const NotificationDashboard = () => {
  const { t } = useTranslation();
  const { companyID } = useAuth();
  const { setRefresh: setGlobalRefresh, enqueueAlert } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const navigation = {
    usage_warning: { url: routing.profile.account_settings, state: { activeTab: 1 } },
    usage_limit: { url: routing.profile.account_settings, state: { activeTab: 1 } },
    invitation_partnership: { url: routing.engagement.collaborate.partnership },
    invitation_collaborate_product: { url: routing.engagement.collaborate.dashboard },
    invitation_collaborate_project: { url: routing.engagement.collaborate.dashboard },
    new_supplier_report: { url: routing.engagement.supplier_directory.dashboard },
    new_client_report: { url: routing.engagement.client_directory.dashboard },
    message: { url: routing.engagement.b2b_directory.dashboard },
    support_request: { url: null }, 
  };

  const getNavigationByContent = (item) => {
    const type = item.type?.toLowerCase() || '';
    const message = item.message?.toLowerCase() || '';

    let nav = null;
    if (navigation[type] && navigation[type].url) nav = { ...navigation[type] };
    else if (message.includes('usage')) nav = { ...navigation.usage_warning };
    else if (message.includes('partnership')) nav = { ...navigation.invitation_partnership };
    else if (message.includes('supplier report')) nav = { ...navigation.new_supplier_report };
    else if (message.includes('collaboration')) nav = { ...navigation.invitation_collaborate_product };
    else if (message.includes('message')) nav = { ...navigation.message };
    else if (message.includes('support')) nav = { ...navigation.support_request };

    // Deep-linking for chat: Add ?chatWith query param
    if (nav && (message.includes('message') || message.includes('collaboration'))) {
        // Robust ID check: find any company ID associated with the notification
        const id = item.from_company_id || item.sender_id || item.resource_id || item.from_id || item.company_id;
        if (id) nav.url = `${nav.url}?chatWith=${id}`;
    }

    const id = item.from_company_id || item.sender_id || item.resource_id || item.from_id || item.company_id;
    const name = message.includes('from') ? message.split('from')[1].trim().split(' ')[0] : (item.sender_name || "Partner");
    
    return { ...nav, id, name };
  };

  const [activeChatId, setActiveChatId] = useState(null);
  const [activeChatName, setActiveChatName] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const chatEndRef = useRef(null);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Fetch messages in modal
  useEffect(() => {
    if (activeChatId) {
      setLoadingChat(true);
      const fetchChat = () => {
        Conversation.get_between_me_and_another_company(activeChatId)
          .then(res => res.json())
          .then(data => {
            setChatMessages(Array.isArray(data) ? [...data].reverse() : []);
            setLoadingChat(false);
          })
          .catch(() => setLoadingChat(false));
      };
      fetchChat();
      const interval = setInterval(fetchChat, 5000);
      return () => clearInterval(interval);
    }
  }, [activeChatId]);

  const handleSend = () => {
    if (!newMessage.trim() || !activeChatId) return;
    Conversation.new(activeChatId, newMessage)
      .then(() => {
        setNewMessage("");
        return Conversation.get_between_me_and_another_company(activeChatId);
      })
      .then(res => res.json())
      .then(data => setChatMessages(Array.isArray(data) ? [...data].reverse() : []))
      .catch(console.error);
  };

  const handleExportPDF = async () => {
    if (!activeChatId || isExporting) return;
    setIsExporting(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18); doc.setTextColor(101, 157, 51);
      doc.text(`Chat History: ${activeChatName}`, 15, 20);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, 28);
      doc.line(15, 32, 195, 32);
      let y = 40;
      chatMessages.forEach((m) => {
        const isMe = m.sender_id === 'me' || (m.sender_id && String(m.sender_id) === String(companyID));
        const sender = isMe ? "Me" : (m.sender_name || activeChatName);
        const time = m.timestamp?.split(" GMT")[0] || m.created_at?.split(" GMT")[0] || "";
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(isMe ? 101 : 107, isMe ? 157 : 107, isMe ? 51 : 107);
        doc.text(`${sender} (${time})`, 15, y); y += 5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(33);
        const splitText = doc.splitTextToSize(m.content, 170); doc.text(splitText, 15, y);
        y += (splitText.length * 5) + 8;
      });
      doc.save(`chat_${activeChatName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) { console.error(err); } finally { setIsExporting(false); }
  };

  const getMyNotifications = () => {
    Notification.get_mine()
      .then((res) => res.json())
      .then((data) => setNotifications(data));
  };

  useEffect(() => getMyNotifications(), [refresh]);

  const read = (ids) => {
    Notification.read(ids).finally(() => {
      setRefresh((r) => !r);
      window.dispatchEvent(new Event('notificationUpdate'));
    });
  };

  const handleActionComplete = (notificationId) => {
    if (notificationId) {
      User.approve_support_access(notificationId).then(() => {
        enqueueAlert({ message: t("Support access approved successfully!"), severity: "success" });
        setGlobalRefresh((r) => !r);
        setRefresh((r) => !r);
        window.dispatchEvent(new Event('notificationUpdate'));
      }).catch(err => {
        enqueueAlert({ message: t("Failed to approve support access"), severity: "error" });
      });
    } else {
      setRefresh((r) => !r);
      window.dispatchEvent(new Event('notificationUpdate'));
    }
  };

  return (
    <Box sx={{ p: 0, animation: 'fadeIn 0.5s ease' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box sx={{ ...tok.sectionHeading, display: 'flex', alignItems: 'center', gap: 1.5, pl: 0, position: 'relative', mb: 0, borderBottom: 'none' }}>
          <Box sx={{ width: '4px', height: '24px', bgcolor: tok.sectionHeading.barColor, borderRadius: '2px' }} />
          <Typography sx={{ fontFamily: tok.sectionHeading.fontFamily, fontSize: tok.sectionHeading.fontSize, fontWeight: tok.sectionHeading.fontWeight, color: tok.sectionHeading.color }}>
            {t("Notification Center")}
          </Typography>
        </Box>
        <Button
          onClick={() => read(notifications.map((n) => n.id))}
          sx={{
            textTransform: "none",
            fontFamily: tok.font.family,
            fontWeight: 700,
            fontSize: '13px',
            color: tok.bg.tabActive,
            borderRadius: tok.radius.button,
            "&:hover": { backgroundColor: alpha(tok.bg.tabActive, 0.1) },
          }}>
          {t("Mark All Read")}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ display: "flex", flexDirection: "column", border: '1px solid', borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden", bgcolor: tok.bg.card }}>
        {notifications.length > 0 ? (
          notifications.map((n, i) => {
            const nav = getNavigationByContent(n);
            const isMsg = n.message?.toLowerCase().includes('message') || n.message?.toLowerCase().includes('collaboration');
            return (
              <Box 
                  key={i} 
                  sx={{ 
                      cursor: (nav?.url || (isMsg && nav.id)) ? "pointer" : "default", 
                      transition: 'all 0.2s ease', borderBottom: i === notifications.length - 1 ? 'none' : '1px solid', borderColor: tok.border.card,
                      '&:hover': (nav?.url || (isMsg && nav.id)) ? { bgcolor: isDark ? alpha(tok.bg.tabActive, 0.05) : alpha(tok.bg.tabActive, 0.02) } : {}
                  }} 
                  onClick={() => {
                      read([n.id]);
                      if (isMsg && nav.id) {
                        // OPEN QUICK CHAT INSTEAD OF NAVIGATING
                        setActiveChatId(nav.id);
                        setActiveChatName(nav.name || "Partner");
                      } else if (nav && nav.url) {
                        navigate(nav.url, { state: nav.state });
                      }
                  }}
              >
                  <MessageNotification notification={n} onActionComplete={handleActionComplete} />
              </Box>
            );
          })
        ) : (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography sx={{ fontFamily: tok.font.family, color: tok.text.muted, fontSize: '0.9rem' }}>{t("No notifications")}</Typography>
          </Box>
        )}
      </Paper>

      {/* ── QUICK CHAT MODAL ── */}
      <Dialog 
        open={Boolean(activeChatId)} 
        onClose={() => setActiveChatId(null)} 
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "12px", border: `2px solid ${tok.bg.tabActive}` } }}
      >
        <DialogTitle sx={{ bgcolor: alpha(tok.bg.tabActive, 0.08), display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
            <Typography sx={{ fontFamily: tok.font.family, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.2, color: tok.bg.tabActive }}>
                <FiMessageSquare /> {t("Chat with")} {activeChatName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={t("Export to PDF")}>
                    <IconButton size="small" onClick={handleExportPDF} disabled={isExporting}><DownloadIcon sx={{ fontSize: 20 }} /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setActiveChatId(null)}><FiX /></IconButton>
            </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '500px', display: 'flex', flexDirection: 'column', bgcolor: isDark ? tok.bg.main : "#f8fafc" }}>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {loadingChat ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: tok.bg.tabActive }} /></Box>
                ) : chatMessages.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                    <Typography sx={{ fontFamily: tok.font.family }}>{t("No messages yet")}</Typography>
                  </Box>
                ) : (
                  chatMessages.map((m, idx) => {
                    const isMe = m.sender_id === 'me' || (m.sender_id && String(m.sender_id) === String(companyID));
                    const brandGreen = tok.bg.tabActive;
                    return (
                      <Box key={idx} sx={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                        <Paper sx={{ p: '10px 16px', borderRadius: isMe ? "18px 18px 0 18px" : "18px 18px 18px 0", background: isMe ? brandGreen : (isDark ? alpha("#fff", 0.1) : "#e5e7eb"), color: isMe ? "#ffffff" : "inherit", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                          <Typography sx={{ fontFamily: tok.font.family, fontSize: 13, lineHeight: 1.5 }}>{m.content}</Typography>
                        </Paper>
                        <Typography sx={{ fontSize: 10, mt: 0.5, color: tok.text.muted, px: 1 }}>{m.timestamp?.split(" GMT")[0] || m.created_at?.split(" GMT")[0]}</Typography>
                      </Box>
                    );
                  })
                )}
                <div ref={chatEndRef} />
            </Box>
            <Box sx={{ p: 2, background: tok.bg.card, borderTop: `1px solid ${tok.border.card}`, display: 'flex', gap: 2 }}>
                <TextField fullWidth placeholder={t("Type your message...")} value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleSend(); }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                <Button variant="contained" onClick={handleSend} disabled={!newMessage.trim()} sx={{ background: tok.bg.tabActive, "&:hover": { background: alpha(tok.bg.tabActive, 0.8) }, borderRadius: "10px", minWidth: 80 }}><FiSend /></Button>
            </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NotificationDashboard;
